/**
 * FR-018.7 — the mapping editor: the one context panel through which a farmer changes,
 * assigns, accepts or adds a source reference (TENANT_CONFIGURATION_UI.md §5, §6.2–6.3).
 *
 * Sections top to bottom: (1) the reference in the source, (2) "Gehört zu <noun>" — the target,
 * (3) harvest years (season kinds only), (4) "Was passiert" — the debounced dry-run preview in
 * sentences, (5) an optional note, (6) the footer `Abbrechen` · `Zuordnung bestätigen`.
 *
 * After Confirm the panel shows the re-home job until `done`. The job is polled by the outer
 * component, which stays mounted while the panel is closed, so closing mid-flight keeps the
 * job (and the Sources re-fetch on `done`, see `useMappingJob`) going.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ContextPanel } from '@hofsync/design-system';
import type { LenseConfig } from '../../../../services/twinService';
import type { PreviewResponse, SourceRowDto, SourceSearchHit } from '../../../../services/sourcesService';
import { useEntitySources, useSourceSearch } from '../../../../hooks/useSources';
import { useSourceSystems } from '../../../../hooks/useSourceSystems';
import type { EditorIntent } from './SourcesPanel';
import { sourcesConfigFor } from './sourcesConfig';
import { validityPhrase } from './validity';
import { buildEditorItem, isSeasonalEditor } from './editorItems';
import { previewSentences, type Translate } from './previewSentences';
import {
    confirmFailureMessage,
    errorKindSentence,
    hasUnrestatedSupersedes,
    useConfirmJob,
    useDebouncedPreview,
    type SubmitConfirm,
} from './usePreviewConfirm';
import { MappingJobView } from './MappingJobView';
import { UnrestatedNotice } from './UnrestatedNotice';
import './MappingEditorPanel.css';

export interface MappingEditorPanelProps {
    open: boolean;
    intent: EditorIntent | null;
    entityType: string;
    entityId: string;
    lens?: LenseConfig;
    onClose(): void;
    onNavigate?(entityType: string, entityId: string): void;
    /** A `shadowed_by_field_span` preview offers `Gruppe ändern`: open the group editor for this shared row. */
    onOpenGroup?(row: SourceRowDto): void;
}

const SEARCH_DEBOUNCE_MS = 250;
const YEAR_SPAN = 6;
const NOTE_MAX = 500;

/** Whether the preview carries `shadowed_by_field_span`, and the span rows it names (top level and per item). */
function shadowingSpanRows(preview: PreviewResponse): { shadowed: boolean; ids: string[] } {
    const all = [...(preview.warnings ?? []), ...(preview.items ?? []).flatMap((i) => i.warnings ?? [])];
    const hits = all.filter((w) => w.code === 'shadowed_by_field_span');
    return { shadowed: hits.length > 0, ids: hits.map((w) => w.mapping_id).filter((id): id is string => !!id) };
}

const numberFmt = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 });

function yearOf(date: string | null | undefined): number | null {
    const m = typeof date === 'string' ? /^(\d{4})/.exec(date) : null;
    return m ? Number(m[1]) : null;
}

function useT(): Translate {
    const { t } = useTranslation();
    return t as unknown as Translate;
}

/** Name + key properties of a target, as a card. */
const TargetCard: React.FC<{ name: string; properties?: SourceSearchHit['properties']; pill?: string | null }> = ({
    name,
    properties,
    pill,
}) => {
    const t = useT();
    const meta = [
        typeof properties?.area_ha === 'number'
            ? t('farm.sources.editor.area', '{{ha}} ha', { ha: numberFmt.format(properties.area_ha) })
            : null,
        properties?.crop || null,
    ].filter((p): p is string => !!p);
    return (
        <div className="mapping-editor__card" data-testid="mapping-editor-target-card">
            <span className="mapping-editor__card-name">{name}</span>
            {meta.length > 0 && <span className="mapping-editor__card-meta">{meta.join(' · ')}</span>}
            {pill && <span className="mapping-editor__pill">{pill}</span>}
        </div>
    );
};

export const MappingEditorPanel: React.FC<MappingEditorPanelProps> = (props) => {
    const { open, intent, entityType, entityId, lens, onClose, onNavigate, onOpenGroup } = props;
    const t = useT();
    // A new intent starts a fresh editor (and forgets the last job); closing (intent → null) keeps it.
    const [lastIntent, setLastIntent] = React.useState<EditorIntent | null>(intent);
    const [session, setSession] = React.useState(0);
    // The confirm lives here, not in the form: a confirm that resolves after Esc still records its
    // job (and so the `done` invalidation still runs) because this component stays mounted.
    const { job, setJob, jobQuery, submitConfirm, submitting } = useConfirmJob();
    if (intent !== lastIntent) {
        setLastIntent(intent);
        if (intent) {
            setSession((s) => s + 1);
            setJob(null);
        }
    }
    const { data } = useEntitySources(entityType, entityId, open && !!intent);
    const fallback = sourcesConfigFor(lens);
    const noun = data?.config?.noun || fallback.noun;

    if (!open || !intent) return null;
    const title = t(`farm.sources.editor.title.${intent.mode}`, t('farm.sources.editor.title.change', 'Zuordnung ändern'));
    const subtitle = data?.object?.name ?? undefined;

    if (job) {
        return (
            <MappingJobView
                title={title}
                subtitle={subtitle}
                job={job}
                jobQuery={jobQuery}
                noun={noun}
                onClose={onClose}
                onNavigate={onNavigate}
            />
        );
    }

    // The shared row on this object that holds a span row the shadow warning names (the row itself, or one
    // of its `span_rows`); else the intent's own row when it is a shared one.
    const groupRowFor = (spanIds: string[]): SourceRowDto | null => {
        const ids = new Set(spanIds);
        const found = data?.rows?.find(
            (r) =>
                r.kind === 'shared' &&
                ((r.mapping_id !== null && ids.has(r.mapping_id)) || (r.span_rows ?? []).some((sr) => ids.has(sr.mapping_id))),
        );
        return found ?? (intent.row?.kind === 'shared' ? intent.row : null);
    };

    return (
        <EditorForm
            key={session}
            intent={intent}
            entityType={entityType}
            entityId={entityId}
            noun={noun}
            masterRole={data?.config?.master_role ?? fallback.master_role ?? null}
            objectName={data?.object?.name ?? null}
            stub={data?.object?.stub ?? null}
            title={title}
            subtitle={subtitle}
            onClose={onClose}
            submitConfirm={submitConfirm}
            submitting={submitting}
            groupRowFor={onOpenGroup ? groupRowFor : undefined}
            onOpenGroup={onOpenGroup}
            onNavigate={onNavigate}
        />
    );
};

interface EditorFormProps {
    intent: EditorIntent;
    entityType: string;
    entityId: string;
    noun: string;
    masterRole: string | null;
    objectName: string | null;
    stub: { source_system: string | null; source_label: string; first_seen: string | null } | null;
    title: string;
    subtitle?: string;
    onClose(): void;
    submitConfirm: SubmitConfirm;
    submitting: boolean;
    groupRowFor?(spanIds: string[]): SourceRowDto | null;
    onOpenGroup?(row: SourceRowDto): void;
    /** Opens the object an unrestated row belongs to (R2). */
    onNavigate?(entityType: string, entityId: string): void;
}

const EditorForm: React.FC<EditorFormProps> = ({
    intent,
    entityType,
    entityId,
    noun,
    masterRole,
    objectName,
    stub,
    title,
    subtitle,
    onClose,
    submitConfirm,
    submitting,
    groupRowFor,
    onOpenGroup,
    onNavigate,
}) => {
    const t = useT();
    const { mode, row } = intent;
    const systems = useSourceSystems();
    const currentYear = new Date().getFullYear();
    const uid = React.useId();

    const [q, setQ] = React.useState('');
    const [debouncedQ, setDebouncedQ] = React.useState('');
    // The needs-assignment card may hand over a target and a year it already chose (assign only).
    const [picked, setPicked] = React.useState<SourceSearchHit | null>(() =>
        mode === 'assign' && intent.target
            ? { entity_id: intent.target.entity_id, name: intent.target.name, properties: intent.target.properties ?? {} }
            : null,
    );
    const [system, setSystem] = React.useState('');
    const [label, setLabel] = React.useState('');
    const [from, setFrom] = React.useState<number>(() => {
        // `change` on an open-ended row that started in the past defaults to this year: the usual
        // change is "from now on", and restating the old start would rewrite history.
        if (mode === 'change' && row && row.valid_to === null && row.valid_from !== null && currentYear > row.valid_from) {
            return currentYear;
        }
        if (mode === 'assign' && intent.validFrom != null) return intent.validFrom;
        return row?.valid_from ?? yearOf(stub?.first_seen) ?? currentYear;
    });
    const [to, setTo] = React.useState<number | null>(() => (mode === 'change' || mode === 'accept' ? (row?.valid_to ?? null) : null));
    const [note, setNote] = React.useState('');
    const [message, setMessage] = React.useState<string | null>(null);
    // Part of a batch landed (partial failure): the preview it was confirmed from is spent, so Confirm
    // stays off until a FRESH preview (a new key: the auto re-preview, or an edit) has resolved.
    const [spentPreviewKey, setSpentPreviewKey] = React.useState<string | null>(null);

    React.useEffect(() => {
        const h = window.setTimeout(() => setDebouncedQ(q.trim()), SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(h);
    }, [q]);
    const searchable = mode === 'change' || mode === 'assign';
    const searchQ = searchable && debouncedQ.length >= 2 ? debouncedQ : '';
    const search = useSourceSearch(entityType, searchQ);

    const seasonal = isSeasonalEditor(mode, row, entityType);
    const result = buildEditorItem({
        mode,
        row,
        stub,
        entityType,
        entityId,
        targetId: picked?.entity_id ?? null,
        system,
        label,
        from: seasonal ? from : null,
        to: seasonal ? to : null,
        intentEntity: { entityType: intent.entityType, entityId: intent.entityId },
    });
    const item = result.status === 'ready' ? result.item : null;
    const items = result.status === 'ready' ? result.items : null;
    const accounted = result.status === 'ready' ? result.accountedIds : [];
    const itemJson = items ? JSON.stringify(items) : null;
    const { current, currentKey, error: currentError, refresh: refreshPreview } = useDebouncedPreview(itemJson);
    const landed = spentPreviewKey !== null && spentPreviewKey === currentKey;

    // A new batch clears the last confirm's sentence; a re-preview of the same batch keeps it.
    React.useEffect(() => {
        setMessage(null);
    }, [itemJson]);

    const targetIsThis = mode === 'add' || mode === 'accept' || (mode === 'change' && !picked);
    const targetName = picked?.name ?? (targetIsThis ? (objectName ?? undefined) : undefined);
    const sentences = current ? previewSentences(current, t, { noun, targetName }) : null;
    // Every mode: the batch may supersede only the row it restates (`change` / `accept`) — and none at all
    // for `assign` / `add`. F-16 supersedes by label, not by target, so any other row would be another
    // object's assignment replaced without this editor ever showing it.
    const surprise = !!current && hasUnrestatedSupersedes(current, accounted);
    const canConfirm =
        !landed &&
        !!items &&
        !!current &&
        !!sentences &&
        !sentences.noop &&
        sentences.blocking.length === 0 &&
        !surprise &&
        !submitting;

    const systemName = (id: string | null | undefined) =>
        (id && systems.find((s) => s.id === id)?.display_name) || id || '';
    const handleConfirm = () => {
        if (!item || !items || !current || landed) return;
        setMessage(null);
        const target = {
            targetType: item.target_type ?? entityType,
            targetId: item.target_ref,
            targetKind: item.target_kind ?? 'entity',
        };
        submitConfirm({ items, preview: current, accounted, note: note.trim() || undefined }, target, (failure) => {
            if (failure.landed) setSpentPreviewKey(currentKey);
            setMessage(confirmFailureMessage(failure, t));
            if (failure.repreview) refreshPreview();
        });
    };

    // `shadowed_by_field_span`: the label belongs to a shared name — offer the group editor.
    const shadow = current ? shadowingSpanRows(current) : null;
    const shadowGroupRow = shadow?.shadowed && groupRowFor ? groupRowFor(shadow.ids) : null;

    // --- (1) Reference in the source ---
    const refSystem = row?.source_system ?? (mode === 'assign' ? stub?.source_system : null);
    const refLabel = row?.source_label ?? (mode === 'assign' ? stub?.source_label : null);
    const referenceSection = (
        <section className="mapping-editor__section">
            <h3 className="mapping-editor__heading">{t('farm.sources.editor.reference', 'Referenz in der Quelle')}</h3>
            {mode === 'add' ? (
                <div className="mapping-editor__fields">
                    <div className="mapping-editor__field">
                        <label htmlFor={`${uid}-system`}>{t('farm.sources.editor.system', 'Quellsystem')}</label>
                        <select id={`${uid}-system`} value={system} onChange={(e) => setSystem(e.target.value)}>
                            <option value="">{t('farm.sources.editor.systemPlaceholder', 'System wählen')}</option>
                            {systems.map((s) => (
                                <option key={s.id} value={s.id}>
                                    {s.display_name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="mapping-editor__field">
                        <label htmlFor={`${uid}-label`}>{t('farm.sources.editor.label', 'Name in der Quelle')}</label>
                        <input id={`${uid}-label`} type="text" value={label} onChange={(e) => setLabel(e.target.value)} />
                    </div>
                </div>
            ) : (
                <>
                    <div className="mapping-editor__reference">
                        {refSystem && <span className="mapping-editor__pill">{systemName(refSystem)}</span>}
                        {refLabel && <span className="mapping-editor__label">{refLabel}</span>}
                    </div>
                    {mode === 'change' && row && (
                        <p className="mapping-editor__meta">
                            {t('farm.sources.editor.currentTarget', 'Derzeit: {{name}} · {{range}}', {
                                name: objectName ?? t('farm.sources.editor.loadingName', '…'),
                                range: validityPhrase(row, t),
                            })}
                        </p>
                    )}
                    {mode === 'assign' && (
                        <p className="mapping-editor__meta">{t('farm.sources.meta.noTargetGeneric', 'noch nicht zugeordnet')}</p>
                    )}
                </>
            )}
        </section>
    );

    // --- (2) Belongs to <noun> ---
    const hits = search.data ?? [];
    const cardName = picked?.name ?? (targetIsThis ? (objectName ?? t('farm.sources.editor.loadingName', '…')) : null);
    const targetSection = (
        <section className="mapping-editor__section">
            <h3 className="mapping-editor__heading">{t('farm.sources.editor.belongsTo', 'Gehört zu {{noun}}', { noun })}</h3>
            {cardName && <TargetCard name={cardName} properties={picked?.properties} pill={masterRole} />}
            {searchable && (
                <div className="mapping-editor__search">
                    <input
                        type="search"
                        aria-label={t('farm.sources.editor.searchLabel', 'Suche')}
                        placeholder={t('farm.sources.editor.searchPlaceholder', '{{noun}} suchen …', { noun })}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />
                    {q.trim().length > 0 && q.trim().length < 2 && (
                        <p className="mapping-editor__meta">{t('farm.sources.editor.searchHint', 'Mindestens zwei Zeichen eingeben.')}</p>
                    )}
                    {searchQ && search.error ? (
                        <p className="mapping-editor__meta">
                            {t('farm.sources.errors.sources_search_unavailable', 'Die Suche ist gerade nicht verfügbar.')}
                        </p>
                    ) : searchQ && search.isFetching && hits.length === 0 ? (
                        <p className="mapping-editor__meta">{t('farm.sources.editor.searching', 'Suche läuft …')}</p>
                    ) : searchQ && search.data && hits.length === 0 ? (
                        <p className="mapping-editor__meta">{t('farm.sources.editor.noHits', 'Keine Treffer.')}</p>
                    ) : null}
                    {searchQ && hits.length > 0 && (
                        <ul className="mapping-editor__hits">
                            {hits.map((hit) => (
                                <li key={hit.entity_id}>
                                    <button
                                        type="button"
                                        className="mapping-editor__hit"
                                        onClick={() => {
                                            setPicked(hit);
                                            setQ('');
                                            setDebouncedQ('');
                                        }}
                                    >
                                        <span className="mapping-editor__hit-name">{hit.name}</span>
                                        {typeof hit.properties?.area_ha === 'number' && (
                                            <span className="mapping-editor__hit-meta">
                                                {t('farm.sources.editor.area', '{{ha}} ha', { ha: numberFmt.format(hit.properties.area_ha) })}
                                            </span>
                                        )}
                                        {hit.properties?.crop && <span className="mapping-editor__hit-meta">{hit.properties.crop}</span>}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </section>
    );

    // --- (3) Harvest years (season kinds only) ---
    const years = new Set<number>();
    for (let y = currentYear - YEAR_SPAN; y <= currentYear + YEAR_SPAN; y++) years.add(y);
    years.add(from);
    if (to !== null) years.add(to);
    const yearList = [...years].sort((a, b) => a - b);
    const oldName = mode === 'change' && picked && picked.entity_id !== entityId ? objectName : null;
    const stripStart = Math.max(Math.min(row?.valid_from ?? from, from), from - 4);
    const strip: number[] = [];
    for (let y = stripStart; y <= from + 2; y++) strip.push(y);
    const yearsSection = seasonal ? (
        <section className="mapping-editor__section">
            <h3 className="mapping-editor__heading">{t('farm.sources.editor.yearsHeading', 'Gilt für Erntejahre')}</h3>
            <div className="mapping-editor__fields mapping-editor__fields--row">
                <div className="mapping-editor__field">
                    <label htmlFor={`${uid}-from`}>{t('farm.sources.editor.from', 'von')}</label>
                    <select
                        id={`${uid}-from`}
                        value={String(from)}
                        onChange={(e) => {
                            const next = Number(e.target.value);
                            setFrom(next);
                            if (to !== null && to < next) setTo(null);
                        }}
                    >
                        {yearList.map((y) => (
                            <option key={y} value={y}>
                                {y}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="mapping-editor__field">
                    <label htmlFor={`${uid}-to`}>{t('farm.sources.editor.to', 'bis')}</label>
                    <select
                        id={`${uid}-to`}
                        value={to === null ? 'open' : String(to)}
                        onChange={(e) => setTo(e.target.value === 'open' ? null : Number(e.target.value))}
                    >
                        <option value="open">{t('farm.sources.editor.open', 'offen')}</option>
                        {yearList
                            .filter((y) => y >= from)
                            .map((y) => (
                                <option key={y} value={y}>
                                    {y}
                                </option>
                            ))}
                    </select>
                </div>
            </div>
            {targetName && (
                <>
                    <div className="mapping-editor__strip" aria-hidden="true">
                        {strip.map((y) => {
                            const moves = y >= from && (to === null || y <= to);
                            const stays = !!oldName && y < from;
                            return (
                                <span
                                    key={y}
                                    className={`mapping-editor__year${moves ? ' mapping-editor__year--move' : stays ? ' mapping-editor__year--stay' : ''}`}
                                >
                                    {y}
                                </span>
                            );
                        })}
                    </div>
                    <p className="mapping-editor__meta">
                        {[
                            oldName && stripStart < from
                                ? t('farm.sources.editor.legendStay', '{{range}} bleiben bei {{name}}', {
                                      range: validityPhrase({ valid_from: stripStart, valid_to: from - 1, validity_kind: 'season' }, t),
                                      name: oldName,
                                  })
                                : null,
                            t('farm.sources.editor.legendMove', 'ab {{year}} → {{name}}', { year: from, name: targetName }),
                        ]
                            .filter(Boolean)
                            .join(' · ')}
                    </p>
                </>
            )}
        </section>
    ) : null;

    // --- (4) What will happen ---
    let body: React.ReactNode;
    if (result.status === 'stale') {
        body = (
            <p className="mapping-editor__reason">
                {t('farm.sources.editor.staleIntent', 'Diese Zuordnung gehört zu einem anderen Objekt. Bitte neu öffnen.')}
            </p>
        );
    } else if (result.status === 'incomplete' && (mode === 'change' || mode === 'accept') && row && !row.source_system) {
        body = (
            <p className="mapping-editor__reason">
                {t(
                    'farm.sources.editor.noSystem',
                    'Diese Schreibweise stammt aus Belegen und hat keine Zuordnung, die sich ändern ließe.',
                )}
            </p>
        );
    } else if (result.status === 'unsupported') {
        body = (
            <p className="mapping-editor__reason">
                {t('farm.sources.editor.unsupported', 'Für diese Objektart lassen sich hier keine Zuordnungen pflegen.')}
            </p>
        );
    } else if (result.status === 'incomplete') {
        body = (
            <p className="mapping-editor__meta">
                {mode === 'add'
                    ? t(
                          'farm.sources.editor.incompleteAdd',
                          'Wählen Sie ein System und geben Sie den Namen ein, um die Auswirkung zu sehen.',
                      )
                    : t('farm.sources.editor.incomplete', 'Wählen Sie ein Ziel, um die Auswirkung zu sehen.')}
            </p>
        );
    } else if (currentError) {
        body = (
            <p className="mapping-editor__reason">
                {errorKindSentence(t, currentError.kind, 'farm.sources.errors.sources_preview_unavailable', 'Die Vorschau ist gerade nicht verfügbar.')}
            </p>
        );
    } else if (!sentences) {
        body = <p className="mapping-editor__meta">{t('farm.sources.editor.previewLoading', 'Die Auswirkung wird berechnet …')}</p>;
    } else if (sentences.noop) {
        body = (
            <p className="mapping-editor__reason">{t('farm.sources.editor.noop', 'Keine Änderung – diese Zuordnung besteht bereits.')}</p>
        );
    } else {
        body = (
            <>
                {sentences.blocking.map((s) => (
                    <p key={s} className="mapping-editor__reason" role="alert">
                        {s}
                    </p>
                ))}
                {surprise && current && (
                    <UnrestatedNotice
                        preview={current}
                        accounted={accounted}
                        entityType={entityType}
                        entityId={entityId}
                        t={t}
                        onNavigate={onNavigate}
                    />
                )}
                {shadowGroupRow && onOpenGroup && (
                    <button type="button" className="mapping-editor__link" onClick={() => onOpenGroup(shadowGroupRow)}>
                        {t('farm.sources.actions.changeGroup', 'Gruppe ändern')}
                    </button>
                )}
                <ul className="mapping-editor__sentences">
                    {sentences.sentences.map((s) => (
                        <li key={s}>{s}</li>
                    ))}
                </ul>
            </>
        );
    }
    const previewSection = (
        <section className="mapping-editor__section" aria-live="polite">
            <h3 className="mapping-editor__heading">{t('farm.sources.editor.whatHappens', 'Was passiert')}</h3>
            {body}
            {message && (
                <p className="mapping-editor__reason" role="alert">
                    {message}
                </p>
            )}
        </section>
    );

    // --- (5) Note ---
    const noteId = `${uid}-note`;
    const noteSection = (
        <section className="mapping-editor__section">
            <label className="mapping-editor__heading" htmlFor={noteId}>
                {t('farm.sources.editor.noteOptional', 'Notiz (optional)')}
            </label>
            <textarea
                id={noteId}
                className="mapping-editor__note"
                maxLength={NOTE_MAX}
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
            />
        </section>
    );

    // --- (6) Footer ---
    const footer = (
        <div className="mapping-editor__footer">
            <button type="button" className="mapping-editor__btn mapping-editor__btn--outline" onClick={onClose}>
                {t('farm.sources.actions.cancel', 'Abbrechen')}
            </button>
            <button
                type="button"
                className="mapping-editor__btn mapping-editor__btn--primary"
                disabled={!canConfirm}
                onClick={handleConfirm}
            >
                {t('farm.sources.actions.confirm', 'Zuordnung bestätigen')}
            </button>
        </div>
    );

    return (
        <ContextPanel isOpen onClose={onClose} title={title} subtitle={subtitle} width="medium" footerSlot={footer}>
            <div className="mapping-editor">
                {referenceSection}
                {targetSection}
                {yearsSection}
                {previewSection}
                {noteSection}
            </div>
        </ContextPanel>
    );
};

export default MappingEditorPanel;
